import { type PropsWithChildren, useCallback, useState } from 'react';

import { EHidratacaoQueimadoType } from '@enums/HidratacaoQueimadoType';

import { HidratacaoQueimadoContext } from './context';
import type { IArea } from './IUseHidratacaoQueimado';

export const HidratacaoQueimadoProvider = ({ children }: PropsWithChildren) => {
  const [age, setAge] = useState<number>(0);
  const [areas, setAreas] = useState<IArea[]>([
    {
      adultPercentage: 4.5,
      childrenPercentage: 9,
      id: 1,
      isSelected: false,
      name: 'Cabeça - frente',
    },
    {
      adultPercentage: 4.5,
      childrenPercentage: 9,
      id: 2,
      isSelected: false,
      name: 'Cabeça - atrás',
    },
    {
      adultPercentage: 9,
      childrenPercentage: 9,
      id: 3,
      isSelected: false,
      name: 'Tórax - frente',
    },
    {
      adultPercentage: 9,
      childrenPercentage: 6.5,
      id: 4,
      isSelected: false,
      name: 'Tórax - atrás',
    },
    {
      adultPercentage: 9,
      childrenPercentage: 9,
      id: 5,
      isSelected: false,
      name: 'Abdome - frente',
    },
    {
      adultPercentage: 9,
      childrenPercentage: 6.5,
      id: 6,
      isSelected: false,
      name: 'Abdome - atrás',
    },
    {
      adultPercentage: 4.5,
      childrenPercentage: 4.5,
      id: 7,
      isSelected: false,
      name: 'MSD - frente',
    },
    {
      adultPercentage: 4.5,
      childrenPercentage: 4.5,
      id: 8,
      isSelected: false,
      name: 'MSD - atrás',
    },
    {
      adultPercentage: 4.5,
      childrenPercentage: 4.5,
      id: 9,
      isSelected: false,
      name: 'MSE - frente',
    },
    {
      adultPercentage: 4.5,
      childrenPercentage: 4.5,
      id: 10,
      isSelected: false,
      name: 'MSE - atrás',
    },
    {
      adultPercentage: 9,
      childrenPercentage: 7,
      id: 11,
      isSelected: false,
      name: 'MID - frente',
    },
    {
      adultPercentage: 9,
      childrenPercentage: 7,
      id: 12,
      isSelected: false,
      name: 'MID - atrás',
    },
    {
      adultPercentage: 9,
      childrenPercentage: 7,
      id: 13,
      isSelected: false,
      name: 'MIE - atrás',
    },
    {
      adultPercentage: 9,
      childrenPercentage: 7,
      id: 14,
      isSelected: false,
      name: 'MIE - atrás',
    },
    {
      adultPercentage: 1,
      childrenPercentage: 0,
      id: 15,
      isSelected: false,
      name: 'Genitália (adulto)',
    },
    {
      adultPercentage: 0,
      childrenPercentage: 2.5,
      id: 16,
      isSelected: false,
      name: 'Nádega D (criança)',
    },
    {
      adultPercentage: 0,
      childrenPercentage: 2.5,
      id: 17,
      isSelected: false,
      name: 'Nádega E (criança)',
    },
  ]);
  const [type, setType] = useState<EHidratacaoQueimadoType>();
  const [SCQTotal, setSCQTotal] = useState<number>(0);
  const [weight, setWeight] = useState<number>(0);

  const handleAge = useCallback(
    (value: number): void => {
      setAge(value);
      const SCQCalculated = areas.reduce((total, item) => {
        if (!item.isSelected) {
          return total + 0;
        }
        if (value >= 14) {
          return total + item.adultPercentage;
        }
        return total + item.childrenPercentage;
      }, 0);
      setSCQTotal(SCQCalculated);
    },
    [areas],
  );

  const handleType = useCallback((value: EHidratacaoQueimadoType): void => {
    setType(value);
  }, []);

  const handleWeight = useCallback((value: number): void => {
    setWeight(value);
  }, []);

  const handleSelectChangeArea = useCallback(
    (area: IArea) => {
      setAreas((current) => {
        const newAreaList = current.map((item) => {
          if (item.id === area.id) {
            return {
              ...item,
              isSelected: !item.isSelected,
            };
          }
          return item;
        });
        const SCQCalculated = newAreaList.reduce((total, item) => {
          if (!item.isSelected) {
            return total + 0;
          }
          if (age >= 14) {
            return total + item.adultPercentage;
          }
          return total + item.childrenPercentage;
        }, 0);
        setSCQTotal(SCQCalculated);
        return newAreaList;
      });
    },
    [age],
  );

  const handleResetSCQ = useCallback((): void => {
    setSCQTotal(0);
    setAreas((current) => {
      return current.map((item) => ({
        ...item,
        isSelected: false,
      }));
    });
  }, []);

  const handleSCQ = useCallback((value: number): void => {
    setSCQTotal(value);
  }, []);

  return (
    <HidratacaoQueimadoContext.Provider
      value={{
        age,
        areas,
        resetSCQ: handleResetSCQ,
        changeAreaSelected: handleSelectChangeArea,
        SCQTotal,
        setAge: handleAge,
        setSCQ: handleSCQ,
        setType: handleType,
        setWeight: handleWeight,
        type: type!,
        weight,
      }}>
      {children}
    </HidratacaoQueimadoContext.Provider>
  );
};
